/**
 * Görüntüleme biçimlendiricileri.
 * Para ve miktar değerleri sunucudan STRING gelir (docs/02 §8); burada yalnızca
 * gösterim için sayıya çevrilir — hesap asla web tarafında yapılmaz.
 */

import { t, tEnum } from '@/i18n';
import type { Unit } from '@biyolink/shared';

const currencyFormatter = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const quantityFormatter = new Intl.NumberFormat('tr-TR', {
  maximumFractionDigits: 3,
});

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "1250.00" → "1.250,00" */
export function formatCurrency(value: string | null): string {
  if (value === null) return '—';
  return currencyFormatter.format(Number(value));
}

/** "312500.00" → "₺312.500,00" (birimsiz tutar: toplam, KDV, komisyon) */
export function formatMoney(value: string | null): string {
  if (value === null) return '—';
  return t('listing.amountWithCurrency', { amount: formatCurrency(value) });
}

/** "1250.00" + TON → "₺1.250,00 / ton" */
export function formatUnitPrice(value: string | null, unit: Unit): string {
  if (value === null) return t('listing.negotiablePrice');
  return t('listing.priceWithUnit', { price: formatCurrency(value), unit: tEnum('units', unit) });
}

/** "250.000" + TON → "250 ton" */
export function formatQuantity(value: string, unit: Unit): string {
  return t('listing.quantityWithUnit', {
    quantity: quantityFormatter.format(Number(value)),
    unit: tEnum('units', unit),
  });
}

export function formatNumber(value: number): string {
  return quantityFormatter.format(value);
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return dateFormatter.format(new Date(iso));
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return dateTimeFormatter.format(new Date(iso));
}

/** Bugünden hedefe kalan tam gün sayısı; geçmişse negatif döner. */
export function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}
